import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { BrandMark } from "./BrandMark";
import {
  denyAnalytics,
  getAnalyticsConsent,
  grantAnalytics,
  initializeAnalytics,
  type AnalyticsConsent,
} from "./analytics";
import { submitLead } from "./contact-client";
import { HeroSystemMap } from "./HeroSystemMap";
import { type Locale, t } from "./i18n";
import { applyPageMetadata } from "./seo";
import {
  type LeadErrors,
  type LeadForm,
  type ModuleId,
  moduleIds,
  officialContacts,
  privacySections,
  resolveOfficialRoute,
  supportTopics,
  termsSections,
  type OfficialRoute,
  validateLeadForm,
} from "./site";

function Arrow({ down = false }: { down?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d={down ? "M10 4v11m0 0 4-4m-4 4-4-4" : "M4 10h11m0 0-4-4m4 4-4 4"} />
    </svg>
  );
}

function Check() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="m4 10 3.6 3.6L16 5.8" />
    </svg>
  );
}

function LanguageGlyph() {
  return (
    <svg className="language-glyph" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="7" />
      <path d="M3.4 10h13.2M10 3c2.7 2.8 2.7 11.2 0 14M10 3c-2.7 2.8-2.7 11.2 0 14" />
    </svg>
  );
}

function ModuleGlyph({ id }: { id: ModuleId }) {
  const paths: Record<ModuleId, ReactNode> = {
    loyalty: <><path d="M8 4.5a7.5 7.5 0 1 0 0 15" /><circle cx="18.5" cy="12" r="2.4" /></>,
    pos: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 9h8M8 14h3" /></>,
    orders: <><path d="M7 4h10l2 17H5L7 4Z" /><path d="M9 8h6" /></>,
    tables: <><path d="M4 10h16M7 10v10m10-10v10" /><path d="M6 5h12v5H6z" /></>,
    reservations: <><rect x="4" y="5" width="16" height="15" rx="3" /><path d="M8 3v4m8-4v4M8 12h3m3 0h2m-8 4h2" /></>,
    inventory: <><path d="m4 8 8-4 8 4v10l-8 4-8-4V8Z" /><path d="m4 8 8 4 8-4m-8 4v10" /></>,
    supply: <><path d="M3 16h13V7H7l-4 4v5Z" /><path d="M16 11h3l2 3v2h-5" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></>,
    invoice: <><path d="M6 3h12v19l-3-2-3 2-3-2-3 2V3Z" /><path d="M9 8h6m-6 4h6m-6 4h3" /></>,
  };
  return <svg className="module-glyph" viewBox="0 0 24 24" aria-hidden="true">{paths[id]}</svg>;
}

function useLocale() {
  const [locale, setLocale] = useState<Locale>(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("collectie-site-locale");
    if (saved === "en" || saved === "zh-Hant" || saved === "de") return saved;
    const browserLanguage = window.navigator.language.toLowerCase();
    if (browserLanguage.startsWith("zh")) return "zh-Hant";
    if (browserLanguage.startsWith("de")) return "de";
    return "en";
  });

  useEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem("collectie-site-locale", locale);
  }, [locale]);

  return { locale, setLocale };
}

function usePageMetadata(locale: Locale, route: OfficialRoute) {
  useEffect(() => {
    applyPageMetadata(locale, route);
  }, [locale, route]);
}

function AnalyticsPreferences({
  locale,
  open,
  onClose,
}: {
  locale: Locale;
  open: boolean;
  onClose: () => void;
}) {
  const [consent, setConsent] = useState<AnalyticsConsent | null>(() =>
    getAnalyticsConsent(),
  );

  if (!open) return null;

  const choose = (next: AnalyticsConsent) => {
    if (next === "granted") grantAnalytics(locale);
    else denyAnalytics();
    setConsent(next);
    onClose();
  };

  return (
    <section
      className="analytics-preferences"
      role="dialog"
      aria-labelledby="analytics-preferences-title"
      aria-describedby="analytics-preferences-body"
    >
      <div className="analytics-preferences__mark" aria-hidden="true">
        <BrandMark reversed />
      </div>
      <div className="analytics-preferences__copy">
        <span>{t(locale, "official.analytics.label")}</span>
        <h2 id="analytics-preferences-title">{t(locale, "official.analytics.title")}</h2>
        <p id="analytics-preferences-body">{t(locale, "official.analytics.body")}</p>
        {consent ? (
          <small>
            {t(
              locale,
              consent === "granted"
                ? "official.analytics.statusGranted"
                : "official.analytics.statusDenied",
            )}
          </small>
        ) : null}
        <a href="/privacy">{t(locale, "official.analytics.privacy")}</a>
      </div>
      <div className="analytics-preferences__actions">
        <button type="button" onClick={() => choose("granted")}>
          {t(locale, "official.analytics.accept")}
        </button>
        <button type="button" onClick={() => choose("denied")}>
          {t(locale, "official.analytics.reject")}
        </button>
      </div>
    </section>
  );
}

function Reveal({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.dataset.visible = "true";
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        element.dataset.visible = "true";
        observer.disconnect();
      },
      { threshold: 0.14, rootMargin: "0px 0px -7%" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className={`reveal ${className}`}>{children}</div>;
}

function LanguagePicker({ locale, setLocale }: { locale: Locale; setLocale: (locale: Locale) => void }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const languages: Array<{ id: Locale; label: string }> = [
    { id: "en", label: t(locale, "language.english") },
    { id: "zh-Hant", label: t(locale, "language.traditionalChinese") },
    { id: "de", label: t(locale, "language.german") },
  ];

  useEffect(() => {
    if (!open) return;
    const closeFromOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeFromKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeFromOutside);
    document.addEventListener("keydown", closeFromKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeFromOutside);
      document.removeEventListener("keydown", closeFromKeyboard);
    };
  }, [open]);

  return (
    <div className="language-picker" ref={rootRef}>
      <button
        type="button"
        className="language-picker__trigger"
        aria-label={t(locale, "language.current", { language: languages.find((item) => item.id === locale)?.label ?? "" })}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
      >
        <LanguageGlyph />
        <span>{languages.find((item) => item.id === locale)?.label}</span>
        <Arrow down />
      </button>
      <div className={`language-picker__menu ${open ? "is-open" : ""}`} role="listbox" aria-label={t(locale, "language.choose")}>
        {languages.map((language) => (
          <button
            type="button"
            role="option"
            aria-selected={language.id === locale}
            key={language.id}
            onClick={() => {
              setLocale(language.id);
              setOpen(false);
            }}
          >
            <span>{language.label}</span>
            {language.id === locale ? <i><Check /></i> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

function Navigation({ locale, setLocale }: { locale: Locale; setLocale: (locale: Locale) => void }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.classList.toggle("menu-open", open);
    return () => document.body.classList.remove("menu-open");
  }, [open]);

  const link = (anchor: string) => `${window.location.pathname === "/" ? "" : "/"}${anchor}`;
  const close = () => setOpen(false);

  return (
    <>
      <header className="site-nav-shell">
        <nav className="site-nav" aria-label={t(locale, "official.nav.primary")}>
          <a className="wordmark" href="/" aria-label={t(locale, "official.nav.home")}>
            <BrandMark />
            <span>Collectie</span>
          </a>
          <div className="nav-links">
            <a href={link("#loyalty")}>{t(locale, "official.nav.product")}</a>
            <a href={link("#platform")}>{t(locale, "official.nav.platform")}</a>
            <a href={link("#pricing")}>{t(locale, "official.nav.pricing")}</a>
          </div>
          <div className="nav-actions">
            <LanguagePicker locale={locale} setLocale={setLocale} />
            <a className="nav-cta" href={link("#contact")}>
              <span>{t(locale, "official.nav.contact")}</span>
              <i><Arrow /></i>
            </a>
            <button
              type="button"
              className={`menu-button ${open ? "is-open" : ""}`}
              aria-label={t(locale, open ? "official.nav.close" : "official.nav.open")}
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
            >
              <span /><span />
            </button>
          </div>
        </nav>
      </header>
      <div className={`menu-overlay ${open ? "is-open" : ""}`} aria-hidden={!open}>
        <div className="menu-overlay__inner">
          <a href={link("#loyalty")} onClick={close}>{t(locale, "official.nav.product")}</a>
          <a href={link("#platform")} onClick={close}>{t(locale, "official.nav.platform")}</a>
          <a href={link("#pricing")} onClick={close}>{t(locale, "official.nav.pricing")}</a>
          <a href={link("#contact")} onClick={close}>{t(locale, "official.nav.contact")}</a>
          <LanguagePicker locale={locale} setLocale={setLocale} />
        </div>
      </div>
    </>
  );
}

function Hero({ locale }: { locale: Locale }) {
  return (
    <section className="hero" id="main-content">
      <div className="hero-glow" aria-hidden="true" />
      <div className="page-grid hero-grid">
        <div className="hero-copy">
          <p className="eyebrow hero-eyebrow"><span />{t(locale, "official.hero.eyebrow")}</p>
          <h1>
            <span>{t(locale, "official.hero.titleLead")}</span>
            <em>{t(locale, "official.hero.titleAccent")}</em>
          </h1>
          <p className="hero-lede">{t(locale, "official.hero.lede")}</p>
          <div className="hero-actions">
            <a className="button button--primary" href="#loyalty">
              <span>{t(locale, "official.hero.primary")}</span><i><Arrow down /></i>
            </a>
            <a className="text-link" href="#platform">
              {t(locale, "official.hero.secondary")}<Arrow />
            </a>
          </div>
        </div>
        <div className="hero-art-shell">
          <div className="hero-art">
            <HeroSystemMap locale={locale} />
          </div>
        </div>
      </div>
      <div className="hero-scroll">
        <span>{t(locale, "official.hero.scroll")}</span>
        <i><Arrow down /></i>
      </div>
    </section>
  );
}

function Principle({ locale }: { locale: Locale }) {
  const items = ["keep", "hardware", "pace"] as const;
  return (
    <section className="principle section-pad">
      <div className="page-grid principle-grid">
        <Reveal className="principle-heading">
          <p className="eyebrow">{t(locale, "official.principle.eyebrow")}</p>
          <h2>{t(locale, "official.principle.title")}</h2>
        </Reveal>
        <Reveal className="principle-copy">
          <p>{t(locale, "official.principle.body")}</p>
        </Reveal>
        <div className="principle-list">
          {items.map((item, index) => (
            <Reveal className="principle-item" key={item}>
              <span className="principle-number">0{index + 1}</span>
              <div>
                <h3>{t(locale, `official.principle.${item}`)}</h3>
                <p>{t(locale, `official.principle.${item}Body`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function RestaurantFilm({ locale }: { locale: Locale }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { threshold: 0.28 },
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const beginFilm = () => {
    const video = videoRef.current;
    setReady(true);
    if (video && video.paused && video.currentTime < 0.05) {
      void video.play().catch(() => undefined);
    }
  };

  return (
    <div className="pixel-film" ref={frameRef}>
      <img
        className="pixel-film__poster"
        src="/restaurant-night-pixel.webp"
        alt={t(locale, "official.restaurantScene.alt")}
        loading="lazy"
        decoding="async"
      />
      <video
        ref={videoRef}
        className={`pixel-film__video${ready ? " is-ready" : ""}`}
        poster="/restaurant-night-pixel.webp"
        preload={shouldLoad ? "auto" : "none"}
        muted
        loop
        playsInline
        aria-hidden="true"
        tabIndex={-1}
        onCanPlay={beginFilm}
      >
        {shouldLoad ? <source src="/restaurant-night-pixel.mp4?v=stabilized-service-3" type="video/mp4" /> : null}
      </video>
    </div>
  );
}

function RestaurantScene({ locale }: { locale: Locale }) {
  return (
    <section className="restaurant-scene section-pad" id="restaurant-scene">
      <div className="page-grid">
        <Reveal className="restaurant-scene__intro">
          <div>
            <p className="eyebrow">{t(locale, "official.restaurantScene.eyebrow")}</p>
            <h2>{t(locale, "official.restaurantScene.title")}</h2>
          </div>
          <p>{t(locale, "official.restaurantScene.body")}</p>
        </Reveal>
        <Reveal className="pixel-frame-shell">
          <figure className="pixel-frame">
            <RestaurantFilm locale={locale} />
            <figcaption>{t(locale, "official.restaurantScene.caption")}</figcaption>
            <span className="pixel-label pixel-label--guest"><i />{t(locale, "official.restaurantScene.guest")}</span>
            <span className="pixel-label pixel-label--kitchen"><i />{t(locale, "official.restaurantScene.kitchen")}</span>
            <span className="pixel-label pixel-label--supply"><i />{t(locale, "official.restaurantScene.supply")}</span>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}

function Loyalty({ locale }: { locale: Locale }) {
  const benefits = ["wallet", "checkout", "truth"] as const;
  const flow = ["Join", "Collect", "Return"] as const;
  return (
    <section className="loyalty section-pad" id="loyalty">
      <div className="loyalty-ambient" aria-hidden="true" />
      <div className="page-grid loyalty-grid">
        <Reveal className="loyalty-intro">
          <p className="eyebrow eyebrow--light">{t(locale, "official.loyalty.eyebrow")}</p>
          <h2>{t(locale, "official.loyalty.title")}</h2>
          <p>{t(locale, "official.loyalty.body")}</p>
        </Reveal>

        <Reveal className="wallet-stage">
          <div className="wallet-shadow" aria-hidden="true" />
          <div className="wallet-card">
            <div className="wallet-topline">
              <div className="wordmark wordmark--reversed"><BrandMark reversed /><span>Collectie</span></div>
              <span>{t(locale, "official.loyalty.cardProgram")}</span>
            </div>
            <div className="wallet-center">
              <small>{t(locale, "official.loyalty.cardReward")}</small>
              <strong>{t(locale, "official.loyalty.cardRewardValue")}</strong>
            </div>
            <div className="stamp-row" aria-hidden="true">
              {Array.from({ length: 7 }, (_, index) => <i key={index} className={index < 5 ? "is-filled" : ""} />)}
            </div>
            <div className="wallet-bottomline">
              <span>{t(locale, "official.loyalty.cardProgress")}</span>
              <span>{t(locale, "official.loyalty.cardMember")}</span>
            </div>
          </div>
          <div className="wallet-scan-pill">
            <span className="scan-corners" aria-hidden="true"><i /><i /><i /><i /></span>
            <span>{t(locale, "official.loyalty.cardHint")}</span>
          </div>
        </Reveal>

        <div className="benefit-list">
          {benefits.map((benefit, index) => (
            <Reveal className="benefit" key={benefit}>
              <span className="benefit-symbol"><i>{index + 1}</i></span>
              <div>
                <h3>{t(locale, `official.loyalty.${benefit}`)}</h3>
                <p>{t(locale, `official.loyalty.${benefit}Body`)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="loyalty-flow">
          {flow.map((step, index) => (
            <div className="flow-step" key={step}>
              <span>0{index + 1}</span>
              <strong>{t(locale, `official.loyalty.flow${step}`)}</strong>
              <p>{t(locale, `official.loyalty.flow${step}Body`)}</p>
              {index < flow.length - 1 ? <i className="flow-line" aria-hidden="true" /> : null}
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function Platform({ locale }: { locale: Locale }) {
  const [active, setActive] = useState<ModuleId>("loyalty");
  const activeIndex = moduleIds.indexOf(active);
  return (
    <section className="platform section-pad" id="platform">
      <div className="page-grid">
        <Reveal className="platform-intro">
          <div>
            <p className="eyebrow">{t(locale, "official.platform.eyebrow")}</p>
            <h2>{t(locale, "official.platform.title")}</h2>
          </div>
          <p>{t(locale, "official.platform.body")}</p>
        </Reveal>

        <Reveal className="intelligence-core">
          <div className="intelligence-core__visual" aria-hidden="true">
            <span className="intelligence-orbit intelligence-orbit--outer" />
            <span className="intelligence-orbit intelligence-orbit--inner" />
            <span className="intelligence-node intelligence-node--forecast" />
            <span className="intelligence-node intelligence-node--exception" />
            <span className="intelligence-node intelligence-node--action" />
            <strong>{t(locale, "official.platform.intelligenceMark")}</strong>
          </div>
          <div className="intelligence-core__copy">
            <span className="intelligence-core__eyebrow">
              <i />{t(locale, "official.platform.intelligenceEyebrow")}
            </span>
            <h3>{t(locale, "official.platform.core")}</h3>
            <p>{t(locale, "official.platform.coreBody")}</p>
            <div className="intelligence-signals">
              {(["Forecast", "Exception", "Action"] as const).map((signal) => (
                <span key={signal}>{t(locale, `official.platform.intelligence${signal}`)}</span>
              ))}
            </div>
            <small>{t(locale, "official.platform.intelligenceGuard")}</small>
          </div>
        </Reveal>

        <Reveal className="module-composer">
          <div className="module-selector" aria-label={t(locale, "official.platform.select")}>
            {moduleIds.map((id, index) => (
              <button
                type="button"
                key={id}
                className={active === id ? "is-active" : ""}
                aria-pressed={active === id}
                onClick={() => setActive(id)}
              >
                <span className="module-index">0{index + 1}</span>
                <span className="module-icon"><ModuleGlyph id={id} /></span>
                <strong>{t(locale, `official.platform.${id}.name`)}</strong>
                <small>{t(locale, id === "loyalty" ? "official.platform.first" : "official.platform.planned")}</small>
              </button>
            ))}
          </div>
          <div className="module-detail" aria-live="polite">
            <div className="module-detail__visual">
              <span className="module-detail__orbit" aria-hidden="true">
                {moduleIds.map((id, index) => (
                  <i key={id} className={index === activeIndex ? "is-active" : ""} style={{ "--position": index } as React.CSSProperties} />
                ))}
                <b><ModuleGlyph id={active} /></b>
              </span>
              <span className="module-detail__status">
                <i />{t(locale, active === "loyalty" ? "official.platform.first" : "official.platform.planned")}
              </span>
            </div>
            <div className="module-detail__copy">
              <span className="module-detail__number">0{activeIndex + 1} / 08</span>
              <h3>{t(locale, `official.platform.${active}.name`)}</h3>
              <strong>{t(locale, `official.platform.${active}.tagline`)}</strong>
              <p>{t(locale, `official.platform.${active}.body`)}</p>
              <div className="pricing-basis">
                <small>{t(locale, "official.platform.pricingBasis")}</small>
                <span>{t(locale, `official.platform.${active}.price`)}</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Pricing({ locale }: { locale: Locale }) {
  return (
    <section className="pricing section-pad" id="pricing">
      <div className="page-grid">
        <Reveal className="pricing-intro">
          <p className="eyebrow eyebrow--light">{t(locale, "official.pricing.eyebrow")}</p>
          <h2>{t(locale, "official.pricing.title")}</h2>
          <p>{t(locale, "official.pricing.body")}</p>
        </Reveal>
        <div className="pricing-layout">
          <Reveal className="pricing-feature-shell">
            <article className="pricing-feature">
              <div className="pricing-feature__top">
                <span>{t(locale, "official.pricing.loyaltyLabel")}</span>
                <BrandMark reversed />
              </div>
              <h3>{t(locale, "official.pricing.loyaltyTitle")}</h3>
              <p>{t(locale, "official.pricing.loyaltyBody")}</p>
              <ul>
                {["includedOne", "includedTwo", "includedThree"].map((key) => (
                  <li key={key}><i><Check /></i><span>{t(locale, `official.pricing.${key}`)}</span></li>
                ))}
              </ul>
              <div className="pricing-feature__footer">
                <div><small>{t(locale, "official.platform.pricingBasis")}</small><strong>{t(locale, "official.pricing.founding")}</strong></div>
                <a className="button button--paper" href="#contact"><span>{t(locale, "official.pricing.cta")}</span><i><Arrow /></i></a>
              </div>
            </article>
          </Reveal>

          <Reveal className="pricing-model-shell">
            <article className="pricing-model">
              <span className="pricing-model__label">{t(locale, "official.pricing.modelLabel")}</span>
              <h3>{t(locale, "official.pricing.modelTitle")}</h3>
              <p>{t(locale, "official.pricing.modelBody")}</p>
              <div className="pricing-lines">
                {moduleIds.slice(1).map((id) => (
                  <div key={id}>
                    <span><ModuleGlyph id={id} />{t(locale, `official.platform.${id}.name`)}</span>
                    <small>{t(locale, `official.platform.${id}.price`)}</small>
                  </div>
                ))}
              </div>
            </article>
          </Reveal>
        </div>
        <p className="pricing-note">{t(locale, "official.pricing.note")}</p>
      </div>
    </section>
  );
}

function Contact({ locale }: { locale: Locale }) {
  const [values, setValues] = useState<LeadForm>({ restaurant: "", email: "", city: "", interest: "loyalty" });
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<LeadErrors>({});
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const idempotencyKey = useRef<string | null>(null);

  const update = <Key extends keyof LeadForm>(key: Key, value: LeadForm[Key]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setSubmissionError(null);
    idempotencyKey.current = null;
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validateLeadForm(values);
    setErrors(nextErrors);
    setSubmissionError(null);
    if (Object.keys(nextErrors).length > 0) {
      window.requestAnimationFrame(() => {
        formRef.current
          ?.querySelector<HTMLElement>('[aria-invalid="true"]')
          ?.focus();
      });
      return;
    }

    setSubmitting(true);
    const submissionKey = idempotencyKey.current ?? crypto.randomUUID();
    idempotencyKey.current = submissionKey;
    const result = await submitLead(
      values,
      locale,
      website,
      submissionKey,
    );
    if (result === "accepted") {
      setComplete(true);
    } else {
      setSubmissionError(
        result === "rate_limited"
          ? "official.contact.rateLimited"
          : "official.contact.submitError",
      );
    }
    setSubmitting(false);
  };

  const sendAnother = () => {
    setValues({ restaurant: "", email: "", city: "", interest: "loyalty" });
    setWebsite("");
    setErrors({});
    setSubmissionError(null);
    idempotencyKey.current = null;
    setComplete(false);
  };

  return (
    <section className="contact section-pad" id="contact">
      <div className="contact-orb" aria-hidden="true"><BrandMark reversed /></div>
      <div className="page-grid contact-grid">
        <Reveal className="contact-copy">
          <p className="eyebrow eyebrow--light">{t(locale, "official.contact.eyebrow")}</p>
          <h2>{t(locale, "official.contact.title")}</h2>
          <p>{t(locale, "official.contact.body")}</p>
        </Reveal>
        <Reveal className="contact-form-shell">
          {complete ? (
            <div className="contact-success" role="status">
              <span className="contact-success__mark"><Check /></span>
              <h3>{t(locale, "official.contact.readyTitle")}</h3>
              <p>{t(locale, "official.contact.readyBody")}</p>
              <button className="text-link" type="button" onClick={sendAnother}>
                {t(locale, "official.contact.again")}<Arrow />
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={submit} noValidate ref={formRef}>
              <label className="website-field" aria-hidden="true">
                <input
                  name="website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
              </label>
              <div className="field field--wide">
                <label htmlFor="restaurant">{t(locale, "official.contact.restaurant")}</label>
                <input
                  id="restaurant"
                  name="restaurant"
                  value={values.restaurant}
                  onChange={(event) => update("restaurant", event.target.value)}
                  placeholder={t(locale, "official.contact.restaurantPlaceholder")}
                  autoComplete="organization"
                  maxLength={120}
                  required
                  disabled={submitting}
                  aria-invalid={Boolean(errors.restaurant)}
                  aria-describedby={errors.restaurant ? "restaurant-error" : undefined}
                />
                {errors.restaurant ? <span className="field-error" id="restaurant-error">{t(locale, errors.restaurant)}</span> : null}
              </div>
              <div className="field">
                <label htmlFor="email">{t(locale, "official.contact.email")}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={(event) => update("email", event.target.value)}
                  placeholder={t(locale, "official.contact.emailPlaceholder")}
                  autoComplete="email"
                  maxLength={254}
                  required
                  disabled={submitting}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email ? <span className="field-error" id="email-error">{t(locale, errors.email)}</span> : null}
              </div>
              <div className="field">
                <label htmlFor="city">{t(locale, "official.contact.city")}</label>
                <input
                  id="city"
                  name="city"
                  value={values.city}
                  onChange={(event) => update("city", event.target.value)}
                  placeholder={t(locale, "official.contact.cityPlaceholder")}
                  autoComplete="address-level2"
                  maxLength={120}
                  required
                  disabled={submitting}
                  aria-invalid={Boolean(errors.city)}
                  aria-describedby={errors.city ? "city-error" : undefined}
                />
                {errors.city ? <span className="field-error" id="city-error">{t(locale, errors.city)}</span> : null}
              </div>
              <div className="field field--wide">
                <label htmlFor="interest">{t(locale, "official.contact.interest")}</label>
                <div className="select-wrap">
                  <select
                    id="interest"
                    name="interest"
                    value={values.interest}
                    onChange={(event) => update("interest", event.target.value as LeadForm["interest"])}
                    disabled={submitting}
                  >
                    <option value="loyalty">{t(locale, "official.contact.interestLoyalty")}</option>
                    <option value="platform">{t(locale, "official.contact.interestPlatform")}</option>
                    <option value="pilot">{t(locale, "official.contact.interestPilot")}</option>
                  </select>
                  <Arrow down />
                </div>
              </div>
              {submissionError ? (
                <p className="form-error field--wide" role="alert">
                  {t(locale, submissionError)}
                </p>
              ) : null}
              <div className="form-footer field--wide">
                <p>
                  <span>{t(locale, "official.contact.privacy")}</span>
                  <a href="/privacy">{t(locale, "official.contact.privacyLink")}</a>
                </p>
                <button
                  className="button button--primary"
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  <span>{t(locale, submitting ? "official.contact.submitting" : "official.contact.submit")}</span><i><Arrow /></i>
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Footer({
  locale,
  onAnalyticsSettings,
}: {
  locale: Locale;
  onAnalyticsSettings: () => void;
}) {
  return (
    <footer className="footer">
      <div className="page-grid footer-main">
        <a className="wordmark wordmark--footer" href="/" aria-label={t(locale, "official.nav.home")}>
          <BrandMark reversed /><span>Collectie</span>
        </a>
        <p>{t(locale, "official.footer.statement")}</p>
        <span className="footer-availability"><i />{t(locale, "official.footer.availability")}</span>
      </div>
      <div className="page-grid footer-bottom">
        <span>{t(locale, "official.footer.copyright", { year: new Date().getFullYear() })}</span>
        <div>
          <a href="/privacy">{t(locale, "official.footer.privacy")}</a>
          <a href="/support">{t(locale, "official.footer.support")}</a>
          <a href="/terms">{t(locale, "official.footer.terms")}</a>
          <button type="button" onClick={onAnalyticsSettings}>
            {t(locale, "official.analytics.settings")}
          </button>
        </div>
      </div>
    </footer>
  );
}

function EmailLink({ address, label }: { address: string; label: string }) {
  return (
    <a className="email-link" href={`mailto:${address}`}>
      <span>{label}</span>
      <strong>{address}</strong>
      <i><Arrow /></i>
    </a>
  );
}

function PrivacyPage({ locale }: { locale: Locale }) {
  return (
    <main className="policy-page" id="main-content">
      <div className="policy-orb" aria-hidden="true" />
      <header className="page-grid policy-hero">
        <div>
          <p className="eyebrow">{t(locale, "official.privacy.eyebrow")}</p>
          <h1>{t(locale, "official.privacy.title")}</h1>
          <p className="policy-lede">{t(locale, "official.privacy.lede")}</p>
        </div>
        <aside className="policy-facts" aria-label={t(locale, "official.privacy.factsLabel")}>
          <dl>
            <div>
              <dt>{t(locale, "official.privacy.controllerLabel")}</dt>
              <dd>{t(locale, "official.privacy.controllerName")}</dd>
            </div>
            <div>
              <dt>{t(locale, "official.privacy.updatedLabel")}</dt>
              <dd>{t(locale, "official.privacy.updatedDate")}</dd>
            </div>
            <div>
              <dt>{t(locale, "official.privacy.contactLabel")}</dt>
              <dd><a href={`mailto:${officialContacts.privacy}`}>{officialContacts.privacy}</a></dd>
            </div>
          </dl>
        </aside>
      </header>

      <div className="page-grid policy-layout">
        <nav className="policy-toc" aria-label={t(locale, "official.privacy.tocLabel")}>
          <strong>{t(locale, "official.privacy.tocLabel")}</strong>
          <ol>
            {privacySections.map((section, index) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {t(locale, `official.privacy.sections.${section.id}.title`)}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="policy-document">
          <div className="policy-summary">
            <span>{t(locale, "official.privacy.summaryLabel")}</span>
            <p>{t(locale, "official.privacy.summaryBody")}</p>
          </div>
          {privacySections.map((section, index) => (
            <section className="policy-section" id={section.id} key={section.id}>
              <div className="policy-section__heading">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h2>{t(locale, `official.privacy.sections.${section.id}.title`)}</h2>
              </div>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>
                  {t(locale, `official.privacy.sections.${section.id}.${paragraph}`)}
                </p>
              ))}
              {section.bullets.length > 0 ? (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>
                      {t(locale, `official.privacy.sections.${section.id}.${bullet}`)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
          <EmailLink
            address={officialContacts.privacy}
            label={t(locale, "official.privacy.emailAction")}
          />
        </article>
      </div>
    </main>
  );
}

function SupportPage({ locale }: { locale: Locale }) {
  return (
    <main className="support-page" id="main-content">
      <div className="support-orb" aria-hidden="true" />
      <header className="page-grid support-hero">
        <p className="eyebrow eyebrow--light">{t(locale, "official.support.eyebrow")}</p>
        <h1>{t(locale, "official.support.title")}</h1>
        <p>{t(locale, "official.support.lede")}</p>
      </header>

      <div className="page-grid support-content">
        <section className="support-contact" aria-labelledby="support-contact-title">
          <div>
            <span>{t(locale, "official.support.contactKicker")}</span>
            <h2 id="support-contact-title">{t(locale, "official.support.contactTitle")}</h2>
            <p>{t(locale, "official.support.contactBody")}</p>
          </div>
          <div className="support-contact__actions">
            <EmailLink
              address={officialContacts.support}
              label={t(locale, "official.support.emailLabel")}
            />
            <EmailLink
              address={officialContacts.privacy}
              label={t(locale, "official.support.privacyLabel")}
            />
          </div>
        </section>

        <aside className="support-safety" aria-labelledby="support-safety-title">
          <span>{t(locale, "official.support.safetyKicker")}</span>
          <h2 id="support-safety-title">{t(locale, "official.support.safetyTitle")}</h2>
          <p>{t(locale, "official.support.safetyBody")}</p>
        </aside>

        <section className="support-prepare" aria-labelledby="support-prepare-title">
          <div>
            <p className="eyebrow">{t(locale, "official.support.prepareKicker")}</p>
            <h2 id="support-prepare-title">{t(locale, "official.support.prepareTitle")}</h2>
          </div>
          <ol>
            {["device", "merchant", "time", "screenshot"].map((item, index) => (
              <li key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{t(locale, `official.support.prepare.${item}`)}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="support-topics" aria-labelledby="support-topics-title">
          <p className="eyebrow">{t(locale, "official.support.topicsKicker")}</p>
          <h2 id="support-topics-title">{t(locale, "official.support.topicsTitle")}</h2>
          <div>
            {supportTopics.map((topic, index) => (
              <article key={topic}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{t(locale, `official.support.topics.${topic}.title`)}</h3>
                  <p>{t(locale, `official.support.topics.${topic}.body`)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="support-response" aria-labelledby="support-response-title">
          <div>
            <p className="eyebrow eyebrow--light">{t(locale, "official.support.responseKicker")}</p>
            <h2 id="support-response-title">{t(locale, "official.support.responseTitle")}</h2>
          </div>
          <div>
            <p>{t(locale, "official.support.responseBody")}</p>
            <a href="/privacy" className="text-link text-link--light">
              {t(locale, "official.support.privacyAction")}<Arrow />
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}

function TermsPage({ locale }: { locale: Locale }) {
  return (
    <main className="policy-page terms-page" id="main-content">
      <div className="policy-orb" aria-hidden="true" />
      <header className="page-grid policy-hero">
        <div>
          <p className="eyebrow">{t(locale, "official.terms.eyebrow")}</p>
          <h1>{t(locale, "official.terms.title")}</h1>
          <p className="policy-lede">{t(locale, "official.terms.lede")}</p>
        </div>
        <aside className="policy-facts" aria-label={t(locale, "official.terms.factsLabel")}>
          <dl>
            <div>
              <dt>{t(locale, "official.terms.providerLabel")}</dt>
              <dd>{t(locale, "official.terms.providerName")}</dd>
            </div>
            <div>
              <dt>{t(locale, "official.terms.updatedLabel")}</dt>
              <dd>{t(locale, "official.terms.updatedDate")}</dd>
            </div>
            <div>
              <dt>{t(locale, "official.terms.contactLabel")}</dt>
              <dd><a href={`mailto:${officialContacts.support}`}>{officialContacts.support}</a></dd>
            </div>
          </dl>
        </aside>
      </header>

      <div className="page-grid policy-layout">
        <nav className="policy-toc" aria-label={t(locale, "official.terms.tocLabel")}>
          <strong>{t(locale, "official.terms.tocLabel")}</strong>
          <ol>
            {termsSections.map((section, index) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {t(locale, `official.terms.sections.${section.id}.title`)}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="policy-document">
          <div className="policy-summary">
            <span>{t(locale, "official.terms.summaryLabel")}</span>
            <p>{t(locale, "official.terms.summaryBody")}</p>
          </div>
          {termsSections.map((section, index) => (
            <section className="policy-section" id={section.id} key={section.id}>
              <div className="policy-section__heading">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h2>{t(locale, `official.terms.sections.${section.id}.title`)}</h2>
              </div>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>
                  {t(locale, `official.terms.sections.${section.id}.${paragraph}`)}
                </p>
              ))}
              {section.bullets.length > 0 ? (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>
                      {t(locale, `official.terms.sections.${section.id}.${bullet}`)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
          <EmailLink
            address={officialContacts.support}
            label={t(locale, "official.terms.emailAction")}
          />
        </article>
      </div>
    </main>
  );
}

function NotFound({ locale }: { locale: Locale }) {
  return (
    <main className="not-found" id="main-content">
      <div className="not-found__mark" aria-hidden="true"><BrandMark reversed /></div>
      <p className="eyebrow eyebrow--light">{t(locale, "official.notFound.eyebrow")}</p>
      <h1>{t(locale, "official.notFound.title")}</h1>
      <p>{t(locale, "official.notFound.body")}</p>
      <a className="button button--paper" href="/"><span>{t(locale, "official.notFound.back")}</span><i><Arrow /></i></a>
    </main>
  );
}

export function App() {
  const { locale, setLocale } = useLocale();
  const route = resolveOfficialRoute(window.location.pathname);
  const [analyticsSettingsOpen, setAnalyticsSettingsOpen] = useState(
    () => getAnalyticsConsent() === null,
  );
  usePageMetadata(locale, route);

  useEffect(() => {
    initializeAnalytics(locale);
  }, [locale]);

  const analyticsPreferences = (
    <AnalyticsPreferences
      locale={locale}
      open={analyticsSettingsOpen}
      onClose={() => setAnalyticsSettingsOpen(false)}
    />
  );
  const footer = (
    <Footer
      locale={locale}
      onAnalyticsSettings={() => setAnalyticsSettingsOpen(true)}
    />
  );

  if (route === "privacy") {
    return (
      <>
        <a className="skip-link" href="#main-content">{t(locale, "official.privacy.skip")}</a>
        <Navigation locale={locale} setLocale={setLocale} />
        <PrivacyPage locale={locale} />
        {footer}
        {analyticsPreferences}
      </>
    );
  }

  if (route === "support") {
    return (
      <>
        <a className="skip-link" href="#main-content">{t(locale, "official.support.skip")}</a>
        <Navigation locale={locale} setLocale={setLocale} />
        <SupportPage locale={locale} />
        {footer}
        {analyticsPreferences}
      </>
    );
  }

  if (route === "terms") {
    return (
      <>
        <a className="skip-link" href="#main-content">{t(locale, "official.terms.skip")}</a>
        <Navigation locale={locale} setLocale={setLocale} />
        <TermsPage locale={locale} />
        {footer}
        {analyticsPreferences}
      </>
    );
  }

  if (route === "notFound") {
    return (
      <>
        <Navigation locale={locale} setLocale={setLocale} />
        <NotFound locale={locale} />
        {analyticsPreferences}
      </>
    );
  }

  return (
    <>
      <a className="skip-link" href="#main-content">{t(locale, "official.hero.primary")}</a>
      <Navigation locale={locale} setLocale={setLocale} />
      <main>
        <Hero locale={locale} />
        <Principle locale={locale} />
        <RestaurantScene locale={locale} />
        <Loyalty locale={locale} />
        <Platform locale={locale} />
        <Pricing locale={locale} />
        <Contact locale={locale} />
      </main>
      {footer}
      {analyticsPreferences}
    </>
  );
}
